import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchAgents, deleteAgent } from '../store/agentsSlice';
import AgentTable from '../components/AgentTable';
import Modal from '../components/Modal';
import api from '../services/api';

function Agents() {
  const dispatch = useDispatch();
  const { agents, loading, total, pages, currentPage } = useSelector((state) => state.agents);
  
  const [filters, setFilters] = useState({
    status: '',
    os_type: '',
    search: '',
    page: 1,
    per_page: 25
  });
  
  const [deleteModal, setDeleteModal] = useState({ open: false, agentId: null });
  const [versionInfo, setVersionInfo] = useState(null);

  useEffect(() => {
    dispatch(fetchAgents(filters));
    fetchVersionInfo();
  }, [dispatch, filters]);

  const fetchVersionInfo = async () => {
    try {
      const res = await api.get('/agents/outdated');
      setVersionInfo(res.data);
    } catch (error) {
      console.error('Failed to fetch version info:', error);
    }
  };

  const handleUpdateAll = async () => {
    if (!window.confirm(`Update ${versionInfo?.outdated_count} outdated agents?`)) return;
    try {
      const res = await api.post('/agents/update-all');
      alert(`Update queued for ${res.data.agents_updated} agents`);
      fetchVersionInfo();
    } catch (error) {
      alert('Failed to queue updates: ' + error.message);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value, page: 1 }));
  };

  const handleDelete = async () => {
    if (!deleteModal.agentId) return;
    try {
      await dispatch(deleteAgent(deleteModal.agentId)).unwrap();
      setDeleteModal({ open: false, agentId: null });
      dispatch(fetchAgents(filters));
    } catch (error) {
      const message = error?.error || error?.detail || 'Failed to delete agent';
      alert(message);
    }
  };

  return (
    <>
      <header className="main-header">
        <h1 className="page-title">Agents</h1>
        <div className="header-actions" style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
          {versionInfo && versionInfo.outdated_count > 0 && (
            <button 
              className="btn btn-primary"
              onClick={handleUpdateAll}
              style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
            >
              🔄 Update {versionInfo.outdated_count} Outdated
            </button>
          )}
          <span style={{ color: 'var(--text-muted)' }}>
            {total} total agents
            {versionInfo && (
              <span style={{ marginLeft: '10px', fontSize: '12px' }}>
                (v{versionInfo.current_version})
              </span>
            )}
          </span>
        </div>
      </header>

      <div className="main-body">
        {/* Filters */}
        <div className="card" style={{ marginBottom: 'var(--spacing-xl)' }}>
          <div className="card-body" style={{ 
            display: 'flex', 
            gap: 'var(--spacing-md)', 
            flexWrap: 'wrap',
            alignItems: 'center'
          }}>
            <input
              type="text"
              className="form-input"
              placeholder="Search agents..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              style={{ maxWidth: '300px' }}
            />
            
            <select
              className="form-select"
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              style={{ maxWidth: '150px' }}
            >
              <option value="">All Status</option>
              <option value="online">Online</option>
              <option value="offline">Offline</option>
            </select>
            
            <select
              className="form-select"
              value={filters.os_type}
              onChange={(e) => handleFilterChange('os_type', e.target.value)}
              style={{ maxWidth: '150px' }}
            >
              <option value="">All OS</option>
              <option value="windows">Windows</option>
              <option value="linux">Linux</option>
              <option value="darwin">macOS</option>
            </select>

            <button 
              className="btn btn-ghost"
              onClick={() => setFilters({ status: '', os_type: '', search: '', page: 1, per_page: 25 })}
            >
              Clear Filters
            </button>
          </div>
        </div>

        {/* Agent Table */}
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Connected Agents</h3>
            <button 
              className="btn btn-sm btn-secondary"
              onClick={() => dispatch(fetchAgents(filters))}
            >
              ↻ Refresh
            </button>
          </div>
          <div className="card-body" style={{ padding: 0 }}>
            {loading ? (
              <div style={{ textAlign: 'center', padding: '60px' }}>
                <div className="loader"></div>
              </div>
            ) : (
              <AgentTable 
                agents={agents} 
                onDelete={(agentId) => setDeleteModal({ open: true, agentId })}
              />
            )}
          </div>
          
          {/* Pagination */}
          {pages > 1 && (
            <div className="card-body" style={{ 
              display: 'flex', 
              justifyContent: 'center', 
              gap: 'var(--spacing-sm)',
              borderTop: '1px solid var(--border-color)'
            }}>
              {Array.from({ length: pages }, (_, i) => i + 1).map(page => (
                <button
                  key={page}
                  className={`btn btn-sm ${page === currentPage ? 'btn-primary' : 'btn-ghost'}`}
                  onClick={() => handleFilterChange('page', page)}
                >
                  {page}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={deleteModal.open}
        onClose={() => setDeleteModal({ open: false, agentId: null })}
        title="Delete Agent"
        footer={
          <>
            <button 
              className="btn btn-ghost"
              onClick={() => setDeleteModal({ open: false, agentId: null })}
            >
              Cancel
            </button>
            <button className="btn btn-danger" onClick={handleDelete}>
              Delete Agent
            </button>
          </>
        }
      >
        <p>Are you sure you want to delete this agent?</p>
        <p style={{ color: 'var(--text-muted)', marginTop: 'var(--spacing-sm)' }}>
          This will remove all tasks, beacons, and exfiltrated data associated with this agent.
        </p>
      </Modal>
    </>
  );
}

export default Agents;
